'use client';

import { Fragment, useMemo } from 'react';

import { I18N_CONF } from '@funkey/locales/config';
import { Icon } from '~/components/primitives';
import Link from 'next/link';
import { UrlPath } from '~/utils/routing';
import { cx } from 'class-variance-authority';
import { useI18n } from '~/features/i18n';
import { usePathname } from 'next/navigation';
import { usePortalCtx } from '~/features/global/portal';

/** NOTE: This component is very URL path structure sensitive.
 * If we were to change our platform url paths this could easily break.
 *
 * NOTE: This component only works in the `app` directory. */
const Breadcrumbs: React.FC<{
    className?: string;
    /** @default false */
    showOnMobile?: boolean;
}> = props => {
    const portal = usePortalCtx();

    const pathNames = useI18nPathname().slice(portal.baseURLPath.length).split('/').filter(Boolean);
    // Activities is not a valid path, our PLP is on /teambuilding
    // TODO: teambuiling should be translatable => https://sprints.zoho.eu/workspace/funkey#P5/itemdetails/I1401
    if (pathNames.includes('activities')) {
        pathNames[pathNames.indexOf('activities')] = 'teambuildings';
    }

    // Don't show breadcrumbs in V1, moved to V2. These need specific mappings, translations, exceptions etc.
    // We also need to solve the problem with url's sometimes having locales and sometimes not
    return null;

    return (
        <div
            className={cx(
                'mb-3',
                'items-center space-x-2 font-medium text-white',
                props.className,
                props.showOnMobile ? 'flex' : 'hidden md:flex',
            )}
        >
            <Link href={portal.baseURLPath}>
                <Icon size="sm" name="home-2" />
            </Link>
            {pathNames.map((link, index) => {
                const href = `${portal.baseURLPath}/${pathNames.slice(0, index + 1).join('/')}`;

                return (
                    <Fragment key={href}>
                        <span className="mx-2 inline-block text-3xl text-white">•</span>
                        <Link
                            // TODO: @Kenji: make this language dependant
                            href={href !== '/categories' ? href : '/'}
                            className="text-normal-case first-letter:capitalize"
                        >
                            {link.replace(/-/g, ' ')}
                        </Link>
                    </Fragment>
                );
            })}
        </div>
    );
};

export default Breadcrumbs;

/** Why? Sometimes the locale isn't present in `usePathname()` so we'll have to work around this.
 *
 * Example: `/path/to/route` | `/nl/path/to/route`
 *
 * @interal Breadcrumbs */
function useI18nPathname() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const pathname = usePathname()!;
    const i18n = useI18n();

    return useMemo(() => {
        const path = new UrlPath(pathname);

        const firstSegment = path.segments[0];

        if (
            firstSegment &&
            firstSegment !== i18n.locale &&
            (I18N_CONF.LANGUAGES.includes(firstSegment as any) ||
                I18N_CONF.LANGUAGES.includes(firstSegment as any))
        ) {
            path.segments.shift();
        }

        path.unshift(i18n.locale);

        return path.toString();
    }, [pathname]);
}
